import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Badge } from '@/components/shadcn/badge'
import { BaseFieldProps } from './types'
import { useState, useCallback, useMemo, useRef, KeyboardEvent } from 'react'
import clsx from 'clsx'
import { DocumentationButton } from './DocumentationButton'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'

export const ArrayInputField: React.FC<BaseFieldProps> = ({ field, formFieldName, control, variant }) => {
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => <ArrayInputFieldInner field={field} formField={formField} variant={variant} />}
    />
  )
}

const ArrayInputFieldInner: React.FC<{
  field: BaseFieldProps['field']
  formField: { value: unknown; onChange: (v: unknown) => void; onBlur: () => void }
  variant: BaseFieldProps['variant']
}> = ({ field, formField, variant }) => {
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const entries = useMemo(() => (formField.value as string[]) || [], [formField.value])

  const addEntry = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      if (trimmed === '') return
      // Prevent duplicates
      if (entries.includes(trimmed)) {
        setInputValue('')
        return
      }
      formField.onChange([...entries, trimmed])
      setInputValue('')
    },
    [entries, formField]
  )

  const removeEntry = useCallback(
    (index: number) => {
      const newEntries = [...entries]
      newEntries.splice(index, 1)
      formField.onChange(newEntries)
    },
    [entries, formField]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        addEntry(inputValue)
      }
      // Allow removing the last entry with Backspace when input is empty
      if (e.key === 'Backspace' && inputValue === '' && entries.length > 0) {
        removeEntry(entries.length - 1)
      }
    },
    [inputValue, addEntry, removeEntry, entries.length]
  )

  const fieldControl = (
    <FormControl>
      <div className="flex flex-col gap-2">
        <div
          className={clsx(
            // 'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 focus-within:border-primary',
            'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-sm border border-input bg-background px-3 py-2 focus-within:border-tangarine-400',
            field.readOnly && 'cursor-not-allowed opacity-50'
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {entries.map((entry, index) => (
            <Badge key={`${entry}-${index}`} variant="secondary" className="gap-1 py-0.5 pr-1">
              <span className="max-w-[200px] truncate">{entry}</span>
              {!field.readOnly && (
                <button
                  type="button"
                  className="ml-0.5 rounded-full outline-none ring-offset-offwhite hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeEntry(index)
                  }}
                >
                  <IconCloseOutline className="size-3" />
                </button>
              )}
            </Badge>
          ))}
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // Add the current value on blur if non-empty
              if (inputValue.trim()) {
                addEntry(inputValue)
              }
              formField.onBlur()
            }}
            disabled={field.readOnly}
            placeholder={
              entries.length === 0 ? `Type and press Enter to add ${field.title.toLowerCase()}` : 'Add more...'
            }
            className="h-auto min-w-[120px] flex-1 border-0 bg-transparent p-0 shadow-none"
          />
        </div>
      </div>
    </FormControl>
  )

  return (
    <FormItem
      className={clsx('flex flex-1 flex-col', {
        hidden: field.widget === 'hidden',
        'flex-row items-center': variant === 'row',
      })}
    >
      <div className="flex justify-start gap-2">
        <FormLabel className={clsx({ 'sr-only': variant === 'row' })}>{field.title}</FormLabel>
        {variant !== 'row' && <DocumentationButton documentation={field.documentation} />}
      </div>
      <FormDescription className={clsx('text-xs', { 'sr-only': variant === 'row' })}>
        {field.description}
      </FormDescription>
      {variant === 'column' ? (
        fieldControl
      ) : (
        <div className="!mt-0 flex items-center gap-x-1 text-muted-foreground">
          <TooltipButton tooltip={field.title}>
            <div className="shrink-0">
              <IconInformationOutline className="size-4" />
            </div>
          </TooltipButton>
          {fieldControl}
        </div>
      )}
      <FormMessage />
    </FormItem>
  )
}
